import React, { useEffect, useState } from 'react';
import { adminApi, bannerUrl } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Pencil, Trash2, Upload, Loader2, ImageIcon, ExternalLink, Link2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const emptyForm = {
  category: 'protection', title: '', description: '', features_text: '',
  banner_url: '', cta_label: 'Learn More', cta_url: '', badge: '', order: 0, active: true,
  q_enabled: false,
  q_type: 'slider',
  q_title: '',
  q_subtitle: '',
  q_field_name: 'answer',
  q_confirm_label: 'Confirm & Continue',
  q_min: 5, q_max: 50, q_step: 5, q_unit: '$', q_default: 10,
  q_options_text: '',
  q_placeholder: '',
  q_input_type: 'text',
};

export default function AdminOffers() {
  const [category, setCategory] = useState('protection');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [shortening, setShortening] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.listOffers(category, true);
      setItems(data || []);
    } catch (e) { toast({ title: 'Failed to load offers' }); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [category]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm, category });
    setOpen(true);
  };
  const openEdit = (o) => {
    setEditing(o);
    const qcfg = o.qualifier || {};
    setForm({
      category: o.category, title: o.title || '', description: o.description || '',
      features_text: (o.features || []).join('\n'),
      banner_url: o.banner_url || '', cta_label: o.cta_label || 'Learn More',
      cta_url: o.cta_url || '', badge: o.badge || '', order: o.order || 0, active: o.active !== false,
      q_enabled: !!qcfg.enabled,
      q_type: qcfg.type || 'slider',
      q_title: qcfg.title || '',
      q_subtitle: qcfg.subtitle || '',
      q_field_name: qcfg.field_name || 'answer',
      q_confirm_label: qcfg.confirm_label || 'Confirm & Continue',
      q_min: qcfg.min ?? 5, q_max: qcfg.max ?? 50, q_step: qcfg.step ?? 5,
      q_unit: qcfg.unit ?? '$', q_default: qcfg.default ?? 10,
      q_options_text: Array.isArray(qcfg.options) ? qcfg.options.map((o) => `${o.label} | ${o.value}`).join('\n') : '',
      q_placeholder: qcfg.placeholder || '',
      q_input_type: qcfg.input_type || 'text',
    });
    setOpen(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminApi.uploadFile(file);
      setForm((f) => ({ ...f, banner_url: res.url }));
      toast({ title: 'Image uploaded' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again.' });
    } finally { setUploading(false); }
  };

  const handleShorten = async () => {
    const raw = (form.cta_url || '').trim();
    if (!raw) return toast({ title: 'Enter a destination URL first' });
    // If it already looks like our own short link, don't re-shorten
    try {
      const u = new URL(raw);
      if (u.host === window.location.host && u.pathname.startsWith('/track/')) {
        return toast({ title: 'Already a short link' });
      }
    } catch { /* not a valid URL yet, still try to shorten */ }
    setShortening(true);
    try {
      const sl = await adminApi.createShortLink({ target_url: raw, title: form.title || undefined });
      setForm((f) => ({ ...f, cta_url: sl.short_url }));
      toast({ title: 'Short link created', description: sl.short_url });
    } catch (e) {
      toast({ title: 'Shorten failed', description: e?.response?.data?.detail || 'Try again.' });
    } finally { setShortening(false); }
  };

  const buildQualifier = () => {
    if (!form.q_enabled) {
      // Persist an explicit "disabled" object rather than null — the PUT endpoint
      // strips None values from its $set, so null would leave the previous config
      // in place and visitors would still see the popup.
      return { enabled: false };
    }
    const base = {
      enabled: true,
      type: form.q_type,
      title: form.q_title || '',
      subtitle: form.q_subtitle || '',
      field_name: (form.q_field_name || 'answer').trim() || 'answer',
      confirm_label: form.q_confirm_label || 'Confirm & Continue',
    };
    if (form.q_type === 'slider') {
      base.min = Number(form.q_min) || 0;
      base.max = Number(form.q_max) || 100;
      base.step = Number(form.q_step) || 1;
      base.unit = form.q_unit || '';
      base.default = Number(form.q_default) || base.min;
    } else if (form.q_type === 'choice') {
      base.options = (form.q_options_text || '')
        .split('\n').map((s) => s.trim()).filter(Boolean)
        .map((line) => {
          const [label, value] = line.split('|').map((p) => (p || '').trim());
          return { label: label || value || '', value: value || label || '' };
        }).filter((o) => o.label);
    } else if (form.q_type === 'input') {
      base.placeholder = form.q_placeholder || '';
      base.input_type = form.q_input_type || 'text';
    }
    return base;
  };

  const handleSave = async () => {
    if (!form.title || !form.description) return toast({ title: 'Title and description are required' });
    if (form.q_enabled && !form.q_title) return toast({ title: 'Please add a question title for the qualifier popup' });
    const payload = {
      category: form.category, title: form.title, description: form.description,
      features: form.features_text.split('\n').map((s) => s.trim()).filter(Boolean),
      banner_url: form.banner_url, cta_label: form.cta_label, cta_url: form.cta_url,
      badge: form.badge, order: Number(form.order) || 0, active: !!form.active,
      qualifier: buildQualifier(),
    };
    setSaving(true);
    try {
      if (editing) await adminApi.updateOffer(editing.id, payload);
      else await adminApi.createOffer(payload);
      setOpen(false);
      await load();
      toast({ title: editing ? 'Offer updated' : 'Offer created' });
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again.' });
    } finally { setSaving(false); }
  };

  const handleDelete = async (o) => {
    if (!window.confirm(`Delete offer "${o.title}"?`)) return;
    try {
      await adminApi.deleteOffer(o.id);
      await load();
      toast({ title: 'Offer deleted' });
    } catch { toast({ title: 'Delete failed' }); }
  };

  const toggleActive = async (o) => {
    try {
      await adminApi.updateOffer(o.id, { active: !o.active });
      await load();
    } catch { toast({ title: 'Update failed' }); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-serif-display text-3xl font-bold text-slate-900">Offers</h1>
          <p className="text-slate-600 mt-1">Manage display-ad style offers for Protect Credit and Financials pages.</p>
        </div>
        <div className="flex gap-3 items-center">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-48 h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="protection">Protect Credit</SelectItem>
              <SelectItem value="financials">Financials</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <Plus className="w-4 h-4 mr-1.5" /> New Offer
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-14 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl bg-white border border-slate-200 p-10 text-center text-slate-500">No offers yet. Click “New Offer” to create one.</div>
      ) : (
        <div className="space-y-3">
          {items.map((o) => (
            <div key={o.id} className="rounded-xl bg-white border border-slate-200 p-4 flex flex-col md:flex-row gap-4 items-start md:items-center">
              <div className="w-full md:w-40 h-24 rounded-md bg-gradient-to-br from-emerald-50 to-teal-50 overflow-hidden shrink-0 flex items-center justify-center relative">
                {o.banner_url ? (
                  <>
                    <img src={bannerUrl(o.banner_url)} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-lg opacity-50" />
                    <img src={bannerUrl(o.banner_url)} alt="" className="relative w-full h-full object-contain object-center p-1.5" />
                  </>
                ) : (
                  <ImageIcon className="w-6 h-6 text-slate-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {o.badge && <span className="text-[10px] uppercase tracking-widest font-bold text-white bg-amber-500 px-2 py-0.5 rounded-full">{o.badge}</span>}
                  <h3 className="font-semibold text-slate-900 truncate">{o.title}</h3>
                  <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full ${o.active ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-slate-100 text-slate-500'}`}>{o.active ? 'Active' : 'Inactive'}</span>
                </div>
                <p className="text-sm text-slate-600 line-clamp-2 mt-1">{o.description}</p>
                {o.cta_url && (
                  <a href={o.cta_url} target="_blank" rel="noopener" className="inline-flex items-center gap-1 text-xs text-emerald-700 mt-1 hover:underline">
                    {o.cta_url} <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={o.active} onCheckedChange={() => toggleActive(o)} />
                <Button variant="outline" size="sm" data-testid={`offer-edit-btn-${o.id}`} onClick={() => openEdit(o)}><Pencil className="w-4 h-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(o)} className="text-red-600 hover:bg-red-50 border-red-200"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Offer' : 'New Offer'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1 h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="protection">Protect Credit</SelectItem>
                    <SelectItem value="financials">Financials</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Badge (e.g. BEST VALUE)</Label>
                <Input value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} className="mt-1 h-10" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 h-10" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 min-h-[80px]" />
            </div>
            <div>
              <Label className="text-xs">Features (one per line)</Label>
              <Textarea value={form.features_text} onChange={(e) => setForm({ ...form, features_text: e.target.value })} className="mt-1 min-h-[100px]" placeholder="Daily Credit Monitoring&#10;Identity Theft Protection" />
            </div>
            <div>
              <Label className="text-xs">Banner Image</Label>
              <div className="mt-1 flex items-start gap-3">
                <div className="w-40 h-24 rounded-md bg-gradient-to-br from-emerald-50 to-teal-50 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0 relative">
                  {form.banner_url ? (
                    <>
                      <img src={bannerUrl(form.banner_url)} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-lg opacity-50" />
                      <img src={bannerUrl(form.banner_url)} alt="" className="relative w-full h-full object-contain object-center p-1.5" />
                    </>
                  ) : (
                    <ImageIcon className="w-6 h-6 text-slate-300" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm cursor-pointer">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    Upload image
                    <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
                  </label>
                  <Input value={form.banner_url} onChange={(e) => setForm({ ...form, banner_url: e.target.value })} className="h-10" placeholder="Or paste an image URL" />
                </div>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Button Label</Label>
                <Input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} className="mt-1 h-10" />
              </div>
              <div>
                <Label className="text-xs">Button / Banner Link</Label>
                <div className="mt-1 flex items-center gap-2">
                  <Input value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} className="h-10 flex-1" placeholder="https://partner.com/offer" />
                  <Button type="button" variant="outline" onClick={handleShorten} disabled={shortening} title="Shorten to a branded /track/ link" data-testid="offer-shorten-btn">
                    {shortening ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Link2 className="w-4 h-4 mr-1" /> Shorten</>}
                  </Button>
                </div>
                <p className="mt-1 text-[11px] text-slate-500">Click <b>Shorten</b> to convert any affiliate URL to a branded <code>theloanalliance.com/track/</code> link. Visitor params are auto-forwarded and the destination stays hidden from users (cloaked).</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Sort Order (lower = first)</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="mt-1 h-10" />
              </div>
              <div className="flex items-end gap-2">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                <Label className="text-sm">Active (visible on site)</Label>
              </div>
            </div>

            {/* Qualifier popup config */}
            <div className={`rounded-xl border p-4 space-y-4 ${form.q_enabled ? 'bg-emerald-50/40 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Label className="text-sm font-semibold text-slate-900">Pre-click qualifier popup</Label>
                  <p className="text-[11px] text-slate-600 mt-0.5">Ask the visitor a short question before redirecting to the offer. The answer is saved to the Customer Journey and appended as a URL param to the affiliate link.</p>
                </div>
                <Switch data-testid="qualifier-enabled-toggle" checked={form.q_enabled} onCheckedChange={(v) => setForm({ ...form, q_enabled: v })} />
              </div>

              {form.q_enabled && (
                <div className="space-y-3">
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Question type</Label>
                      <Select value={form.q_type} onValueChange={(v) => setForm({ ...form, q_type: v })}>
                        <SelectTrigger data-testid="qualifier-type-trigger" className="mt-1 h-10"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="slider">Slider (numeric range)</SelectItem>
                          <SelectItem value="choice">Multiple choice</SelectItem>
                          <SelectItem value="input">Text / number input</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Field name (URL param & DB key)</Label>
                      <Input data-testid="qualifier-field-name-input" value={form.q_field_name} onChange={(e) => setForm({ ...form, q_field_name: e.target.value })} className="mt-1 h-10" placeholder="e.g. deposit_amount" />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Question title</Label>
                    <Input data-testid="qualifier-title-input" value={form.q_title} onChange={(e) => setForm({ ...form, q_title: e.target.value })} className="mt-1 h-10" placeholder="e.g. How much are you planning to deposit in STASH?" />
                  </div>
                  <div>
                    <Label className="text-xs">Subtitle (optional)</Label>
                    <Input data-testid="qualifier-subtitle-input" value={form.q_subtitle} onChange={(e) => setForm({ ...form, q_subtitle: e.target.value })} className="mt-1 h-10" placeholder="Optional helper text below the title" />
                  </div>

                  {form.q_type === 'slider' && (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <div><Label className="text-xs">Min</Label><Input data-testid="qualifier-min-input" type="number" value={form.q_min} onChange={(e) => setForm({ ...form, q_min: e.target.value })} className="mt-1 h-10" /></div>
                      <div><Label className="text-xs">Max</Label><Input data-testid="qualifier-max-input" type="number" value={form.q_max} onChange={(e) => setForm({ ...form, q_max: e.target.value })} className="mt-1 h-10" /></div>
                      <div><Label className="text-xs">Step</Label><Input data-testid="qualifier-step-input" type="number" value={form.q_step} onChange={(e) => setForm({ ...form, q_step: e.target.value })} className="mt-1 h-10" /></div>
                      <div><Label className="text-xs">Default</Label><Input data-testid="qualifier-default-input" type="number" value={form.q_default} onChange={(e) => setForm({ ...form, q_default: e.target.value })} className="mt-1 h-10" /></div>
                      <div><Label className="text-xs">Unit prefix</Label><Input data-testid="qualifier-unit-input" value={form.q_unit} onChange={(e) => setForm({ ...form, q_unit: e.target.value })} className="mt-1 h-10" placeholder="$" /></div>
                    </div>
                  )}
                  {form.q_type === 'choice' && (
                    <div>
                      <Label className="text-xs">Options (one per line, format: <code>Label | value</code>)</Label>
                      <Textarea data-testid="qualifier-options-textarea" value={form.q_options_text} onChange={(e) => setForm({ ...form, q_options_text: e.target.value })} className="mt-1 min-h-[100px]" placeholder="Under $500 | lt_500&#10;$500 – $1,000 | 500_1000&#10;$1,000+ | gt_1000" />
                    </div>
                  )}
                  {form.q_type === 'input' && (
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Placeholder</Label>
                        <Input data-testid="qualifier-placeholder-input" value={form.q_placeholder} onChange={(e) => setForm({ ...form, q_placeholder: e.target.value })} className="mt-1 h-10" />
                      </div>
                      <div>
                        <Label className="text-xs">Input type</Label>
                        <Select value={form.q_input_type} onValueChange={(v) => setForm({ ...form, q_input_type: v })}>
                          <SelectTrigger className="mt-1 h-10"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="text">Text</SelectItem>
                            <SelectItem value="number">Number</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label className="text-xs">Confirm button label</Label>
                    <Input data-testid="qualifier-confirm-label-input" value={form.q_confirm_label} onChange={(e) => setForm({ ...form, q_confirm_label: e.target.value })} className="mt-1 h-10" />
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="offer-save-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? 'Save Changes' : 'Create Offer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
